// Superfície do painel (`spec/17-frontend.md` §4.1): um bundle serve `app.` e `admin.`,
// e quem decide é o host em runtime. `VITE_SURFACE` só vale em dev.

import { APP_ENV } from './config.ts';

export type Surface = 'seller' | 'admin';

const ADMIN_PREFIXES = ['admin.', 'admin-staging.'] as const;

export function surfaceOf(host: string): Surface {
  const normalized = host.toLowerCase();
  return ADMIN_PREFIXES.some((prefix) => normalized.startsWith(prefix)) ? 'admin' : 'seller';
}

/**
 * Em produção e staging quem decide é o host. O override de `VITE_SURFACE` existe só em
 * `local` (linha "local" da tabela de `spec/17` §4.1), no dev server e no `vite preview`
 * do harness de e2e, que roda as duas superfícies em portas diferentes do mesmo host.
 */
export function surface(): Surface {
  const override = import.meta.env.VITE_SURFACE;
  const local = import.meta.env.DEV || APP_ENV === 'local';
  if (local && (override === 'admin' || override === 'seller')) {
    return override;
  }
  return surfaceOf(window.location.host);
}

export function isAdminSurface(): boolean {
  return surface() === 'admin';
}

/** Rotas que o host de admin alcança fora de `/admin/*`. */
const ADMIN_ALLOWED_PATHS = new Set([
  '/login',
  '/no-access',
  '/forgot-password',
  '/reset-password',
]);

export function isAdminPath(pathname: string): boolean {
  return pathname === '/admin' || pathname.startsWith('/admin/');
}

/**
 * `true` quando a rota não existe NESTA superfície e o `__root` responde 404
 * (`spec/17-frontend.md` §4.1): no host de seller, qualquer `/admin/*`; no host de
 * admin, tudo fora de `/admin/*` menos `/login`, `/no-access` e a recuperação de senha.
 */
export function isForeignRoute(pathname: string, current: Surface = surface()): boolean {
  const normalized =
    pathname.length > 1 && pathname.endsWith('/') ? pathname.slice(0, -1) : pathname;
  if (current === 'seller') {
    return isAdminPath(normalized);
  }
  return !isAdminPath(normalized) && !ADMIN_ALLOWED_PATHS.has(normalized);
}
